import React, { useEffect, useState } from "react";

/**
 * @mui componenets
 */
import {
  Box,
  Button,
  Slide,
  Typography,
  Alert,
  IconButton,
  SxProps,
  useTheme,
} from "@mui/material";
import IosShare from "@mui/icons-material/IosShare";

/**
 * Redux
 */
import { useSelector } from "react-redux";
import {
  selectedItinerarySelector,
  ISelectedItinerary,
  setItineraryFromSearch,
} from "redux/selectedItinerary";
import { UseAppDispatch } from "redux/store";

import {
  IFilters,
  ILightItinerary,
  changeAllAirlines,
  filterSelector,
} from "redux/filters";

import { searchQuerySelector } from "redux/searchQuery";
import { useLazyRevalidateQuery } from "redux/services/revalidate";
import { ISearchForm, searchFormSelector } from "redux/searchForm";
import {
  changeFilteredIndexes,
  initialIndexes,
  ISortBy,
  sortSelector,
} from "redux/sort";

/**
 * Utils
 */
import { getAllAirlines, buildLightItineraries } from "./searchResultsUtils";
import { buildQueryString, getShareUrl } from "utils/queryStrings";
import { getFlightCardTranslations } from "utils/translationsUtils";
import {
  getAirlineNames,
  getAirportName,
  getCity,
} from "utils/flightDetailApiUtils";

import { useTranslations } from "hooks/useTranslations";

/**
 * Components
 */
import { RevalidateHandler } from "components/ResponsePopup/RevalidateHandler";
import { GraphicMessage } from "components/GraphicMessage/GraphicMessage";
import {
  discountTextBoxAnimations,
  sortContainerAnimations,
  navigateDelay,
} from "pages/SearchResultsPage/animations.searchResultsPage";
import { SortSearchResults } from "components/Sort/Sort";
import { getFilteredIndexes } from "components/Filters/FilterOptions/FilterLightItineraries";
import { LowpriceCalendarLink } from "../LowPriceCalendar/Navigation/LowpriceCalendarLink";
import { YouthStudentInfo } from "../InfoTextBox/YouthStudentInfoTextBox";
import {
  IItinerary,
  MediaQuery,
  NoFlightsGraphic,
  USPslider,
  CustomAnimation,
  FlightCardDesktop,
  FlightCardMobile,
  SocialShare,
  isDiff,
  pushToDataLayer,
  useConfig,
} from "@thekilroygroup/shared-ui";

/**
 * Third party
 */
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { getCurrency } from "utils/numberUtils";
import { IAppConfigObject } from "appConfig.interfaces";

interface IProps {
  response: {
    itineraries: IItinerary[];
    sortBy: ISortBy;
    searchRequestId: string;
  };
  confirmAndNavigateTo: (path: string) => void;
  pageIsMounted: boolean;
  setPageIsMounted: (value: boolean) => void;
}

export default function SearchResults(props: IProps) {
  /**
   * Props
   */
  const { response, setPageIsMounted } = props;

  /**
   * Hooks
   */
  const theme = useTheme();
  const navigate = useNavigate();
  const { translations } = useTranslations();
  const {
    locale,
    appConfig,
    appConfig: { SHARED_ASSETS_BASE_URL },
  } = useConfig<IAppConfigObject>();

  const { ref, inView, entry } = useInView({
    threshold: 0.75,
  });
  const dispatch = UseAppDispatch();

  const flightCardTranslations = getFlightCardTranslations(translations);
  /**
   * Local state
   */

  const [pageNumber, setPageNumber] = useState(0);
  const [showRevalidateHandler, setShowRevalidateHandler] = useState(false);
  const [lightItineraries, setLightItineraries] = useState<ILightItinerary[]>(
    [],
  );
  const [itineraries, setItineraries] = useState<IItinerary[]>([]);
  const [filteredIndexes, setFilteredIndexes] =
    useState<ISortBy>(initialIndexes);

  /**
   * Redux
   */
  const searchQuery: ISearchForm = useSelector(searchQuerySelector);
  const searchForm: ISearchForm = useSelector(searchFormSelector);
  const filterValues: IFilters = useSelector(filterSelector);
  const { sortType } = useSelector(sortSelector);
  const { fareId }: ISelectedItinerary = useSelector(selectedItinerarySelector);
  const [triggerRevalidate, revalidatResult] = useLazyRevalidateQuery();

  /**
   * Settings
   */
  const pageSize = 10;
  const staggerDelayTime = 100;
  const resultsAmount = filteredIndexes[sortType].length;

  /**
   * Use effects
   */
  useEffect(() => {
    dispatch(
      changeAllAirlines(
        getAllAirlines(response.itineraries, "marketingAirline"),
      ),
    );
    setLightItineraries(buildLightItineraries(response.itineraries));
  }, [response]);

  useEffect(() => {
    if (!lightItineraries.length) {
      setFilteredIndexes(initialIndexes);
      dispatch(changeFilteredIndexes(initialIndexes));
      setItineraries([]);
    } else {
      const filteredIndexes: ISortBy = getFilteredIndexes(
        lightItineraries,
        filterValues,
        response.sortBy,
        fareId,
      );

      setFilteredIndexes(filteredIndexes);
      dispatch(changeFilteredIndexes(filteredIndexes));

      const nextItineraries = buildItineraries(
        response.itineraries,
        filteredIndexes[sortType],
        pageSize * (pageNumber + 1),
      );

      if (isDiff(itineraries, nextItineraries)) {
        setItineraries([]);
        setTimeout(() => {
          setItineraries(nextItineraries);
        });
      }
    }
  }, [
    lightItineraries,
    filterValues.baggageAllowance,
    filterValues.departureTime,
    filterValues.maxDurations,
    filterValues.preferredAirlines,
    filterValues.stops,
    filterValues.travellerTypes,
    sortType,
  ]);

  useEffect(() => {
    setItineraries(
      buildItineraries(
        response.itineraries,
        filteredIndexes[sortType],
        pageSize * (pageNumber + 1),
      ),
    );
  }, [pageNumber]);

  /**
   * Methods
   */
  const buildItineraries = (
    itineraries: IItinerary[],
    indexes: number[],
    amount: number,
  ) => {
    return indexes.slice(0, amount).map((i) => itineraries[i]);
  };

  const handleChooseItinerary = (id: string, buttonType: string) => {
    pushToDataLayer(buttonType, "Choose ticket button");
    const nextSelectedItinerary = response.itineraries.filter(
      (itinerary) => itinerary?.id === id,
    )[0];
    if (nextSelectedItinerary) {
      setShowRevalidateHandler(true);
      dispatch(setItineraryFromSearch(nextSelectedItinerary));
      triggerRevalidate({
        itinerary: nextSelectedItinerary,
        searchQuery,
        searchRequestId: searchForm.searchRequestId,
        config: appConfig,
      });
    }
  };

  const getAnimationDelay = (index: number) => {
    return (index - pageSize * pageNumber) * staggerDelayTime + 1;
  };

  const navigateToLowpriceCalendar = () => {
    setPageIsMounted(false);
    setTimeout(() => {
      navigate(`/calendar?${buildQueryString(searchForm)}`);
    }, navigateDelay);
  };

  const getInfoAlertBox = (itinerary: IItinerary) => {
    if (itinerary.youthStudentMaxAge) {
      return <YouthStudentInfo itinerary={itinerary} />;
    }
  };

  const flightcardProps = {
    ...flightCardTranslations,
    airlineLogosPath: `${SHARED_ASSETS_BASE_URL}/airline-logos`,
    baggageTypesTranslations:
      flightCardTranslations.baggageInfoTranslations.baggageTypes,
    getAirlineNames: getAirlineNames,
    getAirportName: getAirportName,
    getInfoAlertBox: getInfoAlertBox,
    getCity: getCity,
    locale: locale,
    passengerTypesMapTranslations: flightCardTranslations.passengerTypesMap,
    passengerTypesTranslations:
      flightCardTranslations.baggageInfoTranslations.passengerTypes,
    sortType: sortType,
    title: translations.pageTitle,
  };

  const {
    flightCardDesktop,
    sortingContainer,
    showMoreBtnWrapper,
    compressedFlightCard,
    noMoreResults,
  } = sxStyles;

  return (
    <Box>
      {showRevalidateHandler && (
        <RevalidateHandler
          {...props}
          revalidateResult={revalidatResult}
          setIsRevalidated={setShowRevalidateHandler}
        />
      )}
      {resultsAmount > 0 && (
        <>
          {translations.searchResults.textBoxContent.length > 0 && (
            <CustomAnimation
              in={true}
              style={{ transformOrigin: "50% 0 0" }}
              {...discountTextBoxAnimations}
            >
              {translations.searchResults.textBoxContent !==
                "textBoxContent" && (
                <Alert
                  icon={false}
                  variant="neutral"
                  sx={{
                    textAlign: "center",
                    mt: { xs: 2, md: 0 },
                    mb: { xs: 2, md: 3 },
                    p: 2,
                  }}
                >
                  {translations.searchResults.textBoxContent}
                </Alert>
              )}
            </CustomAnimation>
          )}
          {/* <TransitionGroup> */}
          <MediaQuery min="md">
            <CustomAnimation in={true} {...sortContainerAnimations}>
              <Box sx={sortingContainer}>
                {filteredIndexes.penaltyPoints.length &&
                  filteredIndexes.price.length &&
                  filteredIndexes.flightTime.length && (
                    <SortSearchResults
                      currency={getCurrency(response.itineraries[0])}
                      results={{
                        best: lightItineraries[
                          filteredIndexes.penaltyPoints[0]
                        ],
                        cheapest: lightItineraries[filteredIndexes.price[0]],
                        fastest:
                          lightItineraries[filteredIndexes.flightTime[0]],
                      }}
                    />
                  )}
                <SocialShare
                  pageTitle={translations.pageTitle}
                  shareUrl={getShareUrl(searchForm, true, "")}
                  email
                  copyLink
                  socialShareTranslations={translations.socialShare}
                >
                  <Button variant="text" sx={{ px: 0 }}>
                    <IosShare color="primary" sx={{ marginRight: 1 }} />
                    <Typography variant="titleMedium" weight="bold">
                      {translations.searchResults.shareButtonText}
                    </Typography>
                  </Button>
                </SocialShare>
              </Box>
            </CustomAnimation>

            {itineraries.map((itinerary: IItinerary, index: number) => {
              return (
                <CustomAnimation
                  timeout={1000}
                  delay={getAnimationDelay(index)}
                  delayOut={1}
                  in={true}
                  type="fade"
                  easing={theme.transitions.easing.easeInOut}
                  key={`transition-${index}`}
                >
                  {(5 + index) % 10 === 0 && (
                    <Box
                      sx={{
                        paddingY: 1,
                        pr: theme.spacing(28 / 8),
                        mb: -3,
                      }}
                    >
                      <USPslider
                        usps={translations.usp}
                        iconColor={theme.palette.primary.main}
                        pagination
                      />
                    </Box>
                  )}
                  <Box
                    key={`flightcard-${index}`}
                    data-itineray-id={itinerary.id}
                    sx={flightCardDesktop}
                  >
                    <Box overflow="hidden">
                      <FlightCardDesktop
                        {...flightcardProps}
                        isSelected={fareId === itinerary.id}
                        itinerary={itinerary}
                        handleChooseItinerary={handleChooseItinerary}
                        showSource={searchForm.gdssource}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        right: `-48px`,
                        left: "initial",
                        top: "50%",
                        transform: "translate(0, -50%)",
                      }}
                    >
                      <SocialShare
                        pageTitle={translations.pageTitle}
                        shareUrl={getShareUrl(
                          searchForm,
                          fareId === itinerary.id,
                          itinerary.id,
                        )}
                        email
                        copyLink
                        socialShareTranslations={translations.socialShare}
                      >
                        <IconButton>
                          <IosShare color="action" />
                        </IconButton>
                      </SocialShare>
                    </Box>
                  </Box>
                </CustomAnimation>
              );
            })}
          </MediaQuery>
          <MediaQuery max="md">
            <Box>
              {itineraries.map((itinerary: IItinerary, index: number) => {
                return (
                  <CustomAnimation
                    timeout={1000}
                    delay={getAnimationDelay(index)}
                    delayOut={1}
                    in={true}
                    type="fade"
                    easing={theme.transitions.easing.easeInOut}
                    key={`transition-${index}`}
                  >
                    {(5 + index) % 10 === 0 && (
                      <Box sx={{ mb: -3 }}>
                        <USPslider
                          usps={translations.usp}
                          iconColor={theme.palette.primary.main}
                          pagination
                        />
                      </Box>
                    )}
                    <Box
                      key={`flightcard-${index}`}
                      data-itineray-id={itinerary.id}
                      ref={fareId === itinerary.id ? ref : null}
                    >
                      <FlightCardMobile
                        {...flightCardTranslations}
                        {...flightcardProps}
                        isSelected={fareId === itinerary.id}
                        itinerary={itinerary}
                        shareUrl={getShareUrl(
                          searchForm,
                          fareId === itinerary.id,
                          itinerary.id,
                        )}
                        handleChooseItinerary={handleChooseItinerary}
                      />
                    </Box>
                    {index === 2 && (
                      <LowpriceCalendarLink
                        handleNavigate={navigateToLowpriceCalendar}
                        searchForm={searchForm}
                        variant="outlined"
                        sx={{
                          maxWidth: "300px",
                          mx: "auto",
                          width: "100%",
                          mt: 4,
                          size: "large",
                          mb: 4,
                        }}
                      />
                    )}
                  </CustomAnimation>
                );
              })}
              {itineraries.length !== 0 && itineraries[0]?.id === fareId && (
                <Box sx={compressedFlightCard}>
                  <Slide
                    direction="up"
                    in={!inView && entry !== undefined}
                    mountOnEnter={true}
                    unmountOnExit={true}
                  >
                    <Box>
                      <FlightCardMobile
                        {...flightcardProps}
                        compressed
                        isSelected={true}
                        itinerary={itineraries[0]}
                        shareUrl={getShareUrl(searchForm, true, fareId)}
                        handleChooseItinerary={handleChooseItinerary}
                      />
                    </Box>
                  </Slide>
                </Box>
              )}
            </Box>
          </MediaQuery>
        </>
      )}
      {itineraries.length !== 0 &&
        resultsAmount > pageSize * (pageNumber + 1) && (
          <Box sx={showMoreBtnWrapper}>
            <Button
              variant="outlined"
              onClick={() => setPageNumber(pageNumber + 1)}
            >
              {translations.searchResults.showMoreResults}
            </Button>
          </Box>
        )}
      {itineraries.length !== 0 &&
        resultsAmount <= pageSize * (pageNumber + 1) &&
        resultsAmount !== 0 && (
          <CustomAnimation in={true} {...discountTextBoxAnimations}>
            <Typography variant="h2" sx={noMoreResults}>
              {translations.searchResults.noMoreResults}
            </Typography>
          </CustomAnimation>
        )}
      {resultsAmount === 0 && (
        <GraphicMessage
          title={translations.searchResults.noResults.title}
          subTitle={translations.searchResults.noResults.subTitle}
        >
          <NoFlightsGraphic />
        </GraphicMessage>
      )}
    </Box>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  flightCardDesktop: {
    position: "relative",
    mr: { xs: 3.5, xl: 0 },
  },
  sortingContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  showMoreBtnWrapper: {
    width: 1,
    display: "flex",
    justifyContent: "space-around",
    py: 6,
    px: 0,
  },
  compressedFlightCard: {
    position: "fixed",
    bottom: 3,
    left: 2.5,
    right: 2.5,
    zIndex: 11,
  },
  noMoreResults: {
    textAlign: "center",
    m: 5,
  },
};
